import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers';
import { AssignmentStatus } from '@/modules/planner/types/assignment-status';

/**
 * Assignment status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class AssignmentStatusConfirmed implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-green-600 text-green-600',
    };
  }
}

export class AssignmentStatusUnconfirmed implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-red-600 text-red-600',
    };
  }
}

export class AssignmentStatusDraft implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-gray-500 text-gray-500',
    };
  }
}

export class AssignmentStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createAssignmentStatus = (status: string): BaseStatus => {
  switch (status) {
    case AssignmentStatus.Confirmed:
      return new AssignmentStatusConfirmed();
    case AssignmentStatus.Unconfirmed:
      return new AssignmentStatusUnconfirmed();
    case AssignmentStatus.Draft:
      return new AssignmentStatusDraft();
    default:
      return new AssignmentStatusUnknown();
  }
};

/**
 * Get localized status text for Assignment status.
 * This function should be used with translation function (t) to get localized text.
 */
export const getAssignmentStatusText = (status: AssignmentStatus): string => {
  switch (status) {
    case AssignmentStatus.Confirmed:
      return i18n.t('authenticated.planner:statuses.confirmed');
    case AssignmentStatus.Unconfirmed:
      return i18n.t('authenticated.planner:statuses.unconfirmed');
    case AssignmentStatus.Draft:
      return i18n.t('authenticated.planner:statuses.draft');
    default:
      return i18n.t('authenticated.planner:statuses.unknown');
  }
};

export const getAssignmentStatusColor = (status: AssignmentStatus) => {
  switch (status) {
    case AssignmentStatus.Confirmed:
      return 'green-600';
    case AssignmentStatus.Unconfirmed:
      return 'red-600';
    case AssignmentStatus.Draft:
      return 'gray-500';
    default:
      return 'mutted-foreground';
  }
};

/**
 * Preconfigured mapper for assignment status options.
 * Maps an array of AssignmentStatus values to option objects with label, value, and color.
 */
export const getAssignmentStatusOptions = createStatusOptionsMapper<AssignmentStatus>({
  getStatusColor: getAssignmentStatusColor,
  getStatusText: getAssignmentStatusText,
});
